import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { AlertTriangle, Trash2, CheckCircle } from 'lucide-react';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * In-app account deletion — satisfies Apple App Store Guideline 5.1.1(v)
 * ("apps that support account creation must let a user initiate deletion of
 * their account from within the app").
 *
 * VERA is a HIPAA business associate storing clinical records on behalf of
 * practices, so accounts can't be hard-deleted on demand — records may carry
 * legal/treatment retention obligations. This initiates a VERIFIED deletion
 * REQUEST (the compliant path Apple accepts for regulated data): it logs a
 * request tagged source:'in-app' with the signed-in user's identity, which the
 * team then verifies and completes. Same backing collection as the public
 * data-deletion form.
 */
export const DeleteAccountSection: React.FC = () => {
  const { user } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const submit = async () => {
    setStatus('submitting');
    setError(null);
    try {
      await addDoc(collection(db, 'dataDeletionRequests'), {
        name: user.name || '',
        email: user.email || '',
        description:
          'In-app account deletion request — please delete my account and associated personal data.',
        status: 'pending',
        source: 'in-app',
        userId: user.id,
        companyId: user.companyId || '',
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setConfirming(false);
    } catch (err) {
      console.error('Account deletion request failed:', err);
      setStatus('error');
      setError('Could not submit your request. Please email support@veraaba.com.');
    }
  };

  return (
    <div
      className="card"
      style={{ marginTop: 'var(--spacing-6)', borderColor: 'var(--color-error, #dc2626)' }}
    >
      <div style={{ padding: 'var(--spacing-6)' }}>
        <h2
          className="card-title"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', color: 'var(--color-error, #dc2626)' }}
        >
          <AlertTriangle size={18} /> Delete Account
        </h2>

        {status === 'success' ? (
          <div
            role="status"
            style={{
              marginTop: 'var(--spacing-3)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-2)',
              color: 'var(--color-success, #166534)',
              fontSize: 'var(--text-sm)',
            }}
          >
            <CheckCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Request received. We'll verify your identity and contact you at{' '}
              <strong>{user.email}</strong> within 5 business days.
            </span>
          </div>
        ) : (
          <>
            <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              Requesting deletion starts the removal of your account and personal
              data. Because VERA stores clinical records on behalf of your practice,
              some records may be retained, restricted, or de-identified where the
              law requires; we verify your identity and confirm what applies before
              completing the request (typically within 30 days).
            </p>

            {error && (
              <div style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-error, #dc2626)', fontSize: 'var(--text-sm)' }}>
                {error}
              </div>
            )}

            {!confirming ? (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                style={{
                  marginTop: 'var(--spacing-4)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  background: 'transparent',
                  color: 'var(--color-error, #dc2626)',
                  border: '1px solid var(--color-error, #dc2626)',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 'var(--font-semibold)',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={16} /> Request Account Deletion
              </button>
            ) : (
              <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)' }}>
                  Submit a deletion request for {user.email}?
                </span>
                <button
                  type="button"
                  onClick={submit}
                  disabled={status === 'submitting'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    backgroundColor: 'var(--color-error, #dc2626)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: 'var(--font-semibold)',
                    cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
                    opacity: status === 'submitting' ? 0.6 : 1,
                  }}
                >
                  <Trash2 size={16} /> {status === 'submitting' ? 'Submitting…' : 'Yes, request deletion'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={status === 'submitting'}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountSection;
